import { useState, useEffect, useCallback, useMemo } from 'react';
import { saveProtocol as saveProtocolApi, toggleProtocolLock as toggleLockApi, listPatientDoctors } from '../components/API';
import { cacheFiles } from '../util/FileCacheUtil';
import { computeBloodTestFingerprint, legacyBloodTestFingerprint, findProtocolFile, PROTOCOL_FILE_RE, protocolEraDate } from '../util/ProtocolUtil';
import { hasActiveDoctor as hasActiveDoctorFn, effectiveLocked as computeEffectiveLocked } from '../util/protocolPermissions';

/**
 * Hook for protocol persistence and doctor lock status.
 *
 * Lock state is cached in protocol.json (`locked` field) so the patient
 * sees the correct state immediately from localStorage — no API flash.
 *
 * API lookup (`listPatientDoctors`) only fires when:
 *   - Patient has NO saved protocol (need to know before enabling Generate)
 *   - Saved protocol says locked (background verify)
 * If saved protocol says unlocked, we trust it — no API call.
 *
 * Doctor saves automatically set `locked: true` in protocol.json and
 * sync the manage-access backend.
 */
export function useProtocol({ userFiles, setUserFiles, patientId, doctorViewMode, user, bloodTestFiles, normalizeTestName, namesReady = true }) {
    const [savedProtocol, setSavedProtocol] = useState(null);
    const [protocolLocked, setProtocolLocked] = useState(false);
    const [hasActiveDoctorState, setHasActiveDoctor] = useState(false);
    const [lockedByName, setLockedByName] = useState(null);
    const [lockLoading, setLockLoading] = useState(false);

    // Extract protocol.json from userFiles whenever they change
    useEffect(() => {
        if (!userFiles || !Array.isArray(userFiles)) {
            setSavedProtocol(null);
            return;
        }
        const protocolFile = findProtocolFile(userFiles);
        if (protocolFile) {
            try {
                const data = typeof protocolFile.data === 'string'
                    ? JSON.parse(protocolFile.data)
                    : protocolFile.data;
                setSavedProtocol(data);
            } catch (err) {
                console.error('Error parsing protocol file:', err);
                setSavedProtocol(null);
            }
        } else {
            setSavedProtocol(null);
        }
    }, [userFiles]);

    // Patient lock check — reads protocol directly from userFiles to avoid
    // depending on savedProtocol state (which lags by one render cycle).
    //   - Protocol says unlocked → trust cache, skip API
    //   - Protocol says locked → init immediately, verify in background
    //   - No protocol → check API before enabling Generate
    //   - Legacy protocol (no locked field) → treat as unlocked, skip API
    useEffect(() => {
        if (doctorViewMode || !user) return;
        if (userFiles === null) return; // still loading files

        // Read protocol directly from userFiles (avoids effect ordering issues)
        const protocolFile = Array.isArray(userFiles)
            ? findProtocolFile(userFiles)
            : null;
        let protocolData = null;
        if (protocolFile) {
            try {
                protocolData = typeof protocolFile.data === 'string'
                    ? JSON.parse(protocolFile.data)
                    : protocolFile.data;
            } catch { /* ignore parse errors */ }
        }

        // Protocol with explicit locked field
        if (protocolData?.locked != null) {
            setProtocolLocked(protocolData.locked);
            // Optimistically mirror the cached flag; background verify corrects it.
            setHasActiveDoctor(!!protocolData.locked);
            if (protocolData.lockedByName) setLockedByName(protocolData.lockedByName);
            if (!protocolData.locked) return; // unlocked — trust it, no API
            // locked — fall through to background verify
        }

        // Legacy protocol without locked field → unlocked, no API
        if (protocolData && protocolData.locked == null) {
            setHasActiveDoctor(false);
            return;
        }

        // Reaches here when:
        //   1. No protocol → must check API before enabling Generate
        //   2. Protocol says locked → background verify
        let cancelled = false;
        setLockLoading(true);
        (async () => {
            try {
                const result = await listPatientDoctors();
                if (cancelled) return;
                const doctors = result?.doctors || [];
                const active = hasActiveDoctorFn(doctors);
                const activeDoctor = doctors.find(d => !d.expiresAt && !d.expired);
                setProtocolLocked(active);
                setHasActiveDoctor(active);
                setLockedByName(activeDoctor?.doctorName || null);
            } catch (err) {
                console.error('Error checking protocol lock status:', err);
            } finally {
                if (!cancelled) setLockLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [doctorViewMode, user, userFiles]);

    // Single source of truth for the effective lock state. A stale `locked`
    // flag left behind after a doctor is removed resolves to unlocked.
    const effectiveLockedValue = useMemo(
        () => computeEffectiveLocked({ protocolLocked, hasActiveDoctor: hasActiveDoctorState }),
        [protocolLocked, hasActiveDoctorState]
    );

    // Fingerprint: compare saved protocol's snapshot against current blood test files
    const currentFingerprint = useMemo(
        () => computeBloodTestFingerprint(bloodTestFiles, normalizeTestName),
        [bloodTestFiles, normalizeTestName]
    );

    // Protocols saved before the fingerprint became content-based carry the old
    // key-joined form. Accept either, so switching the scheme does not prompt every
    // existing user to regenerate against labs that have not changed.
    const legacyFingerprint = useMemo(
        () => legacyBloodTestFingerprint(bloodTestFiles),
        [bloodTestFiles]
    );

    const canRegenerate = useMemo(() => {
        if (!savedProtocol) return false;
        if (!currentFingerprint) return false;
        // normalizeTestName is callable before its map arrives and returns raw lab
        // names until then, so the fingerprint it produces is a different string
        // for the same labs. Claiming "newer results" during that window would be
        // the very false alarm this fingerprint exists to prevent.
        if (!namesReady) return false;
        const saved = savedProtocol.bloodTestFingerprint;
        return saved !== currentFingerprint && saved !== legacyFingerprint;
    }, [savedProtocol, currentFingerprint, legacyFingerprint, namesReady]);

    // Save protocol to S3 via upload endpoint, then update local state.
    // Doctor saves automatically set locked: true and sync the backend.
    const saveProtocol = useCallback(async (protocolData) => {
        try {
            const targetId = patientId || null;
            const dataWithFingerprint = {
                ...protocolData,
                bloodTestFingerprint: currentFingerprint,
                // Era preservation: edits keep the existing generatedAt (same era file);
                // only callers that intentionally start a new era (regeneration) pass a fresh one.
                generatedAt: protocolData.generatedAt ?? savedProtocol?.generatedAt,
            };

            // Auto-lock when doctor saves (unless caller explicitly set locked)
            if (doctorViewMode && dataWithFingerprint.locked == null) {
                dataWithFingerprint.locked = true;
                dataWithFingerprint.lockedByName = user?.displayName || null;
            }

            const result = await saveProtocolApi(dataWithFingerprint, targetId);

            if (result?.data) {
                const protocolObj = typeof result.data === 'string'
                    ? JSON.parse(result.data)
                    : result.data;
                setSavedProtocol(protocolObj);

                // Auto-lock: also sync manage-access backend if not already locked
                if (doctorViewMode && !protocolLocked && targetId) {
                    setProtocolLocked(true);
                    toggleLockApi(targetId, true).catch(err =>
                        console.error('Auto-lock backend sync failed:', err)
                    );
                }

                // Update userFiles context locally (same pattern as upload/delete).
                // Remove ALL protocol files (a new era supersedes the old era's file).
                // Drop compliance files ONLY on a genuine era CHANGE — compared by
                // ERA, never by file key: a same-era edit (same-day overwrite of
                // protocol_<era>.json) and a legacy plain→dated migration flip both
                // keep the existing compliance pairing untouched. When the previous
                // era can't be determined, compliance is kept (never reset on edit).
                const protocolKey = result.key || `protocol_${protocolEraDate(protocolObj.generatedAt)}.json`;
                const newEra = protocolKey.match(/^protocol_(\d{8})\.json$/)?.[1] || null;
                setUserFiles(prevFiles => {
                    const files = prevFiles || [];
                    const previousProtocol = findProtocolFile(files);
                    let prevEra = previousProtocol?.key.match(/^protocol_(\d{8})\.json$/)?.[1] || null;
                    if (!prevEra && previousProtocol) {
                        try {
                            const prevData = typeof previousProtocol.data === 'string'
                                ? JSON.parse(previousProtocol.data)
                                : previousProtocol.data;
                            if (prevData?.generatedAt) prevEra = protocolEraDate(prevData.generatedAt);
                        } catch { /* unknown era -> keep compliance */ }
                    }
                    const eraChanged = prevEra != null && newEra != null && prevEra !== newEra;
                    const filtered = files.filter(f =>
                        !PROTOCOL_FILE_RE.test(f.key)
                        && !(eraChanged && /^compliance(_\d{8})?\.json$/.test(f.key))
                    );
                    const updated = [...filtered, { key: protocolKey, data: protocolObj }];
                    const cacheId = targetId || user?.userId;
                    if (cacheId) cacheFiles(cacheId, updated);
                    return updated;
                });
            }
            return result;
        } catch (error) {
            console.error('Error saving protocol:', error);
            throw error;
        }
    }, [patientId, user, setUserFiles, currentFingerprint, doctorViewMode, protocolLocked, savedProtocol]);

    // Toggle lock (doctor only) — also persists in protocol.json
    const toggleLock = useCallback(async (targetPatientId, lock) => {
        setLockLoading(true);
        try {
            const protocolData = savedProtocol || { entries: [] };
            const doctorName = user?.displayName || null;
            const envelope = {
                ...protocolData,
                locked: lock,
                lockedByName: lock ? doctorName : null,
                bloodTestFingerprint: currentFingerprint,
            };

            const [toggleResult, saveResult] = await Promise.all([
                toggleLockApi(targetPatientId, lock),
                saveProtocolApi(envelope, targetPatientId),
            ]);

            if (toggleResult) {
                setProtocolLocked(lock);
            }
            if (saveResult?.data) {
                const protocolObj = typeof saveResult.data === 'string'
                    ? JSON.parse(saveResult.data)
                    : saveResult.data;
                setSavedProtocol(protocolObj);
                const protocolKey = saveResult.key || `protocol_${protocolEraDate(protocolObj.generatedAt)}.json`;
                setUserFiles(prevFiles => {
                    const files = prevFiles || [];
                    const filtered = files.filter(f => !PROTOCOL_FILE_RE.test(f.key));
                    const updated = [...filtered, { key: protocolKey, data: protocolObj }];
                    const cacheId = targetPatientId || user?.userId;
                    if (cacheId) cacheFiles(cacheId, updated);
                    return updated;
                });
            }
            return toggleResult;
        } catch (error) {
            console.error('Error toggling protocol lock:', error);
            throw error;
        } finally {
            setLockLoading(false);
        }
    }, [savedProtocol, currentFingerprint, user, setUserFiles]);

    return {
        savedProtocol,
        setSavedProtocol,
        protocolLocked,
        setProtocolLocked,
        hasActiveDoctor: hasActiveDoctorState,
        effectiveLocked: effectiveLockedValue,
        lockedByName,
        lockLoading,
        saveProtocol,
        toggleLock,
        canRegenerate,
    };
}
